import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { weekendApi } from "../../../../../api/weekend";
import { HGBugsnagNotify } from "../../../../../helpers/bugsnag";
import { containsFuzzy } from "../../../../../helpers/locale";
import { Button, Modal, Spinner, Table } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { parse } from "csv-parse/browser/esm/sync";
import { Congregation, PublicSpeakerInput, Speaker, TalkHistory } from "../../../../../types/scheduling/weekend";
import { detectDateFormat, ISODateFormat } from "../../../../../helpers/dateHelpers";
import dayjs from "../../../../../helpers/dayjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import QueryKeys from "../../../../../api/queryKeys";
import { ImportStats } from "../../../../../types/import";
import urljoin from "url-join";
import { FileEarmarkSpreadsheet } from "react-bootstrap-icons";
import { HGContext } from "../../../../../helpers/globals";
import { NameFormat } from "../../../../../types/cong";

type CSVTalkHistory = {
  Date: string;
  Congregation: string;
  PublicSpeaker: string;
  OutlineNumber: string;
};

export default function ImportTalkHistoryModal(props: {
  show: boolean;
  setShow: (show: boolean) => void;
  langGroupId: number;
}) {
  const { t } = useTranslation();
  const ctx = useContext(HGContext);
  const nameFmt = ctx.globals.nameFmt;
  const [historyFile, setHistoryFile] = useState<File>();
  const [errorMessage, setErrorMessage] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);
  const [history, setHistory] = useState<TalkHistory[]>([]);
  const [stats, setStats] = useState<ImportStats>();
  const allSpeakersQuery = useQuery({
    queryKey: [QueryKeys.WMAllSpeakers, props.langGroupId],
    queryFn: () => weekendApi.getSpeakersAll(props.langGroupId),
  });
  const queryClient = useQueryClient();

  const onDropHistory = useCallback(
    (acceptedFiles: ConcatArray<File>) => {
      if (acceptedFiles.length < 1) return;
      const csvFile = acceptedFiles[0];
      if (!csvFile) return;
      setHistoryFile(csvFile);
      setErrorMessage("");

      const reader = new FileReader();
      reader.onabort = () => console.log("csv file reading was aborted");
      reader.onerror = () => console.error("csv file reading has failed");
      reader.onload = async () => {
        const textStr = reader.result as string;

        try {
          const hist = parse(textStr, {
            columns: true,
            skip_empty_lines: true,
            relax_column_count: true,
          }) as CSVTalkHistory[];
          const dateFormat = detectDateFormat(hist.map((h) => [h.Date?.trim() ?? ""]));
          if (!dateFormat) {
            throw new Error("could not determine date format");
          }
          setHistory(
            // use flatMap to remove items that aren't valid
            hist.flatMap((h): TalkHistory[] => {
              const dateStr = h.Date?.trim() || "";
              if (!dateStr) return [];
              const djs = dayjs(dateStr, dateFormat);
              const date = djs.isValid() && djs.year() > 2000 ? djs.format(ISODateFormat) : "";
              if (!date) {
                console.warn("unable to parse date", dateStr, dateFormat);
                return [];
              }

              const speakerName = h.PublicSpeaker?.trim();
              if (!speakerName) return [];
              const talkNumber = Number(h.OutlineNumber) || 0;

              return [
                {
                  date: date,
                  speaker: talkHistorySpeaker(
                    allSpeakersQuery.data ?? [],
                    speakerName,
                    h.Congregation?.trim() || "",
                    talkNumber,
                    nameFmt,
                  ),
                  talkNumber: talkNumber,
                },
              ];
            }),
          );
        } catch (err: any) {
          console.error("unexpected error with Talk History CSV", err);
          setErrorMessage(t("popup.error.title.invalid-input"));
        }
      };
      reader.readAsText(csvFile);
    },
    [allSpeakersQuery, nameFmt, t],
  );

  const { getRootProps: getHistoryRootProps, getInputProps: getHistoryInputProps } = useDropzone({
    onDrop: onDropHistory,
  });

  const handleClose = () => {
    props.setShow(false);
    setShowSpinner(false);
    setHistoryFile(undefined);
    setHistory([]);
    setErrorMessage("");
    setStats(undefined);
  };

  const doImport = async () => {
    setShowSpinner(true);
    try {
      const stats = await weekendApi.importTalkHistory(history, props.langGroupId);
      setStats(stats);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.WMAllSpeakers, props.langGroupId],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.WMSpeakers],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.WMCSchedule],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.WMCSchedules],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.WMTalksByCong],
        }),
      ]);
    } catch (err: any) {
      console.error("wm talk history import error", err);
      HGBugsnagNotify("wmTalkHistoryImport", err);
      setErrorMessage(t("import.status.failed"));
    } finally {
      setShowSpinner(false);
    }
  };

  return (
    <Modal show={props.show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{t("schedules.weekend.stats.speaker-talk-history")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <a href={urljoin(import.meta.env.VITE_PUBLIC_URL, "static/talk_history_import.csv")}>
          <FileEarmarkSpreadsheet /> {t("schedules.weekend.add-speaker-csv-template")}
        </a>
        <section className="container mt-4 mb-4">
          <div {...getHistoryRootProps({ className: "dropzone" })}>
            <input {...getHistoryInputProps()} disabled={!allSpeakersQuery.isSuccess} />
            {!historyFile ? "CSV" : `${historyFile.name} (${history.length})`}
          </div>
        </section>
        {!!errorMessage && <p className="text-danger bold mb-2">{errorMessage}</p>}
        {!!stats && (
          <div>
            <p>{t("import.status.results")}</p>
            <Table>
              <thead>
                <tr>
                  <th>{t("import.stats.imported")}</th>
                  <th>{t("import.stats.updated")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{stats.added}</td>
                  <td>{stats.updated}</td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {showSpinner && <Spinner animation="border" className="me-2" />}
        {!stats ? (
          <Button variant="primary" onClick={doImport} disabled={showSpinner}>
            {t("nav.congregation.import")}
          </Button>
        ) : (
          <Button variant="secondary" onClick={handleClose}>
            {t("popup.error.button.ok")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

function talkHistorySpeaker(
  allSpeakers: Speaker[],
  speakerName: string,
  speakerCong: string,
  talkNumber: number,
  nameFmt: NameFormat,
): PublicSpeakerInput | undefined {
  if (!speakerName?.trim()) return undefined;

  const parsedName = parseSpeakerName(speakerName.trim(), nameFmt);
  if (!parsedName.firstName && !parsedName.lastName) return undefined;

  let matchingCongregation: Congregation | undefined = undefined;
  if (speakerCong?.trim()) {
    const congName = speakerCong.trim();
    const matchingCongregations = allSpeakers
      .map((s) => s.congregation)
      .filter((cong): cong is Congregation => cong !== null)
      .filter(
        (cong) => containsFuzzy(cong.name, congName) || containsFuzzy(cong.custom_name ?? "", congName),
      );

    const matchingCongregationIds = new Set(matchingCongregations.map((c) => c.id));
    if (matchingCongregationIds.size === 1) {
      matchingCongregation = matchingCongregations.find((c) => c.id === matchingCongregationIds.values().next().value);
    }

    const candidateSpeakers = matchingCongregation
      ? allSpeakers.filter((s) => s.congregation?.id === matchingCongregation?.id)
      : allSpeakers;

    const matchingSpeakers = candidateSpeakers.filter((speaker) => {
      const firstNameMatch =
        !parsedName.firstName || containsFuzzy(speaker.firstname ?? "", parsedName.firstName);

      const lastNameMatch =
        !parsedName.lastName || containsFuzzy(speaker.lastname ?? "", parsedName.lastName);
      return firstNameMatch && lastNameMatch;
    });

    if (matchingSpeakers.length === 1) {
      const speaker = matchingSpeakers[0];
      if (speaker) {
        // we're forcing this because all we really want to convey is the speaker ID of the matching existing speaker
        return {
          id: speaker.id,
        } as PublicSpeakerInput;
      }
    }

    return {
      id: 0,
      congregation: matchingCongregation?.id || null,
      congregation_name: speakerCong,
      firstname: parsedName.firstName,
      lastname: parsedName.lastName,
      out: true,
      public_talks: [{ number: talkNumber }],
    } as PublicSpeakerInput;
  }
}

function parseSpeakerName(name: string, nameFmt: NameFormat): { firstName: string; lastName: string } {
  const trimmedName = name.trim();
  if (!trimmedName) return { firstName: "", lastName: "" };

  // Check if name contains a comma, which indicates "Last, First" format
  if (trimmedName.includes(",")) {
    const [lastPart, firstPart] = trimmedName.split(",").map((s) => s.trim());
    if (!lastPart && !firstPart) return { firstName: "", lastName: "" };
    if (!firstPart) return { firstName: "", lastName: lastPart || "" };

    // Extract first word from firstPart as the first name
    const firstWords = firstPart.split(/\s+/);
    return {
      firstName: firstWords[0] || "",
      lastName: lastPart || "",
    };
  }

  // No comma - use name format setting
  const parts = trimmedName.split(/\s+/);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0] || "", lastName: "" };

  if (nameFmt === NameFormat.First) {
    // "First Last" format
    return {
      firstName: parts[0] || "",
      lastName: parts[parts.length - 1] || "",
    };
  } else {
    // NameFormat.Last - "Last First" format (no comma)
    return {
      firstName: parts[parts.length - 1] || "",
      lastName: parts[0] || "",
    };
  }
}
